import { useEffect, useMemo, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { SuccessStoryPicker } from '../../../../pickers/success-story/SuccessStoryPicker';
import type { SuccessStory } from '../../../../types/success-stories';
import type { QuestTemplateFirestore, QuestTemplateStoryFirestore } from '../../services/questTypes';
import { buildResourceUsageMap } from '../../utils/questUsageUtils';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormFields } from './QuestFormFields';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import { useQuestForm } from './useQuestForm';

interface StorySpecificFormData {
    imageUrl: string;
    successStoryId: string;
}

interface StoryQuestFormProps {
    questTemplate?: QuestTemplateStoryFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateStoryFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateStoryFirestore) => void;
    onCancel: () => void;
    allQuestTemplates: QuestTemplateFirestore[];
}

export const StoryQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel, allQuestTemplates }: StoryQuestFormProps) => {
    // Use the common quest form hook
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    // Story-specific form data
    const [specificFormData, setSpecificFormData] = useState<StorySpecificFormData>({
        imageUrl: '',
        successStoryId: '',
    });

    // Build usage map for success stories
    const storyUsageMap = useMemo(() => {
        return buildResourceUsageMap(allQuestTemplates, 'successStoryId');
    }, [allQuestTemplates]);

    // Initialize story-specific data
    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                imageUrl: questTemplate.imageUrl,
                successStoryId: questTemplate.successStoryId,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean) => {
        // Check if it's a common field or specific field
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.imageUrl.trim()) {
            return 'Image URL is required for story quests';
        }
        if (!specificFormData.successStoryId.trim()) {
            return 'Success Story ID is required for story quests';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateStoryFirestore>): QuestTemplateStoryFirestore => {
        return {
            ...baseData,
            type: 'story',
            maxProgress: 1,
            imageUrl: specificFormData.imageUrl,
            successStoryId: specificFormData.successStoryId,
        } as QuestTemplateStoryFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="story"
                disableProgressType={true}
                onChange={handleChange}
            />

            <QuestFormSectionDivider title="Story-specific fields" />

            {/* Story-specific fields */}
            <SuccessStoryPicker
                value={specificFormData.successStoryId}
                onChange={(story: SuccessStory | null) => {
                    if (story) {
                        setSpecificFormData(prev => ({
                            ...prev,
                            successStoryId: story.id,
                            imageUrl: story.featuredImageUrl
                        }));
                    } else {
                        setSpecificFormData(prev => ({
                            ...prev,
                            successStoryId: '',
                            imageUrl: ''
                        }));
                    }
                }}
                label="Success Story *"
                required
                usageMap={storyUsageMap}
            />

            {/* Display selected story image (read-only) */}
            {specificFormData.imageUrl && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Selected Story Image
                    </label>
                    <div className="border border-gray-300 rounded-md p-4 bg-gray-50 flex justify-center items-center">
                        <img
                            src={specificFormData.imageUrl}
                            alt={specificFormData.successStoryId}
                            className="max-w-full h-auto rounded-md w-64 h-64 object-cover"
                        />
                    </div>
                </div>
            )}

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};
